import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Form, ProgressBar } from "react-bootstrap";
import {
    FaFileAlt,
    FaClock,
    FaCheckCircle,
    FaTimesCircle,
    FaStopwatch,
    FaEye,
    FaDownload,
} from "react-icons/fa";
import "./DashboardStats.css";
import { AgGridReact } from "ag-grid-react";
import {
    CircleInfo,
    Cross,
    Download,
    FileCsv,
    FileExcel,
    FilePdf,
    Filter,
    Search,
} from "../../../../../../../components/icons/Icons";
import Widgets from "../../../../../../../components/Widgets";
import TableSkeleton from "../../../../../../../components/table/TableSkeleton.js";
import CustomPagination from "../../../../../../../components/CustomPagination";
import BarChart from "../../../../../../../components/Charts/BarChart.js";
import DeductionBreakDown from "../../../../../../../components/Charts/DeductionBreakDown.js";
import {
    getCapitalizeWord,
    getInitials,
    getInitialColor,
    currentFynencialYear,
    useSelect,
} from "../../../../../../../services/commonServices";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    BranchList,
    DepartmentList,
    RegimetList,
    FinancialYear,
} from "../../../../../../../components/FormElements/index.js";
import {
    getTdsAllData,
    initAllTds,
} from "../../../../../../../redux/slices/tdsSlice.js";
import { getTDSDashboardPath } from "../../../../../../../services/config.js";
import { toast } from "react-toastify";

// ─── Helper: extract employee list from API response ──────────────────────────
// Your API returns employees inside data.employeeList.employees (same endpoint)
const extractEmployees = (apiData) => {
    if (!apiData) return [];
    // Primary path based on actual API response shape
    if (Array.isArray(apiData?.employeeList?.employees)) {
        return apiData.employeeList.employees;
    }
    // Fallbacks just in case shape changes
    if (Array.isArray(apiData?.employees)) return apiData.employees;
    if (Array.isArray(apiData?.data?.employeeList?.employees)) return apiData.data.employeeList.employees;
    return [];
};

// ─── Helper: map slab-wise distribution to chart format ───────────────────────
// API returns data.slabWiseDistribution.oldRegime / newRegime arrays
const extractSlabData = (apiData, regime = "old") => {
    if (!apiData?.slabWiseDistribution) return [];
    const key = regime === "old" ? "oldRegime" : "newRegime";
    const slabs = apiData.slabWiseDistribution[key];
    if (!Array.isArray(slabs)) return [];
    return slabs.map((s) => ({
        name: s.range,
        amount: s.taxAmount,
        count: s.count,
        percentage: 0, // not provided by API for slabs
    }));
};

const Dashboard = () => {
    useEffect(() => {
        localStorage.removeItem("tdsUser");
    }, []);

    // NOTE: Your API stores userId as user_id in localStorage
    // The payload must send userId: 2, not user_id or extra fields
    const user = JSON.parse(localStorage.getItem("user_data")) || {};
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tdsStore = useSelect("tds");

    const userId = user.user_id || user.userId || user.id || null;

    const [tdsAllRecord, setTdsAllRecord] = useState({
        totalTDSSubmitted: 0,
        totalTDSTrend: "+0% vs last month",
        pendingVerifications: 0,
        approved: 0,
        approvedRate: "0%",
        rejected: 0,
        rejectedRate: "0%",
        avgProcessingTime: "0",
        panPresentPercentage: 0,
        panPresentCount: 0,
        panMissingPercentage: 0,
        panMissingCount: 0,
        slabWiseDistribution: { oldRegime: [], newRegime: [] },
        deductionBreakdown: [],
        processingTrends: [],
        financialYear: currentFynencialYear(),
        totalEmployees: 0,
    });

    // Employee list comes from the SAME dashboard API response — no second API call
    const [tdsEmpList, setTdsEmpList] = useState([]);
    const [loading, setLoading] = useState(true);

    const [state, setState] = useState({
        financialYear: currentFynencialYear(), // "2026-27"
        departmentId: "",
        regimeType: "old",
        branchId: "",
    });

    const [regimeType, setRegimeType] = useState("old");
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [isExporting, setIsExporting] = useState(false);

    const paginationPageSize = 10;
    const paginationPageSizeSelector = [10, 20, 50, 100];

    const onGridReady = (params) => setGridApi(params.api);

    // ─── Single API call — handles both dashboard stats AND employee list ──────
    const fetchDashboardData = useCallback(() => {
        setLoading(true);
        dispatch(
            getTdsAllData({
                path: getTDSDashboardPath,
                data: {
                    userId: userId,
                    financialYear: state.financialYear,   // "2026-27"
                    departmentId: state.departmentId || null,
                    regimeType: state.regimeType === "old" ? null : state.regimeType,
                    branchId: state.branchId || null,
                },
            })
        );
    }, [dispatch, userId, state.financialYear, state.departmentId, state.regimeType, state.branchId]);

    // ─── On mount only ────────────────────────────────────────────────────────
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // ─── Listen for external refresh events ───────────────────────────────────
    useEffect(() => {
        const handleModuleRefresh = () => fetchDashboardData();
        window.addEventListener("tdsModuleRefresh", handleModuleRefresh);
        return () => window.removeEventListener("tdsModuleRefresh", handleModuleRefresh);
    }, [fetchDashboardData]);

    // ─── Handle Redux store updates ───────────────────────────────────────────
    useEffect(() => {
        if (tdsStore.tdsAllSuccess && tdsStore.tdsAllData) {
            const api = tdsStore.tdsAllData;

            console.log("Dashboard API response:", api);

            // ── Summary stats ──
            setTdsAllRecord({
                totalTDSSubmitted: Number(api.summary?.totalTDSSubmitted) || 0,
                totalTDSTrend: api.summary?.totalTDSTrend || "+0% vs last month",
                pendingVerifications: Number(api.summary?.pendingVerifications) || 0,
                approved: Number(api.summary?.approved) || 0,
                approvedRate: api.summary?.approvedRate || "0%",
                rejected: Number(api.summary?.rejected) || 0,
                rejectedRate: api.summary?.rejectedRate || "0%",
                avgProcessingTime: api.summary?.avgProcessingTime || "0",

                // PAN compliance
                panPresentPercentage: Number(api.panCompliance?.panPresentPercentage) || 0,
                panPresentCount: Number(api.panCompliance?.panPresentCount) || 0,
                panMissingPercentage: Number(api.panCompliance?.panMissingPercentage) || 0,
                panMissingCount: Number(api.panCompliance?.panMissingCount) || 0,

                // Slab-wise distribution (from slabWiseDistribution, NOT deductionBreakdown)
                slabWiseDistribution: api.slabWiseDistribution || { oldRegime: [], newRegime: [] },

                // deductionBreakdown — API returned [] in your sample, keep as is
                deductionBreakdown: Array.isArray(api.deductionBreakdown)
                    ? api.deductionBreakdown
                    : [],

                processingTrends: api.processingTrends || [],
                financialYear: api.metadata?.financialYear || currentFynencialYear(),
                totalEmployees: Number(api.metadata?.totalEmployees) || 0,
            });

            // ── Employee list — extracted from SAME response ──────────────────
            // API shape: data.employeeList.employees[]
            const employees = extractEmployees(api);
            console.log(`Extracted ${employees.length} employees from dashboard response`);
            setTdsEmpList(employees);

            setLoading(false);
            dispatch(initAllTds());
        }

        if (tdsStore.tdsAllError) {
            toast.error(tdsStore.tdsAllError || "Failed to load dashboard data");
            setLoading(false);
        }
    }, [tdsStore.tdsAllSuccess, tdsStore.tdsAllData, tdsStore.tdsAllError, dispatch]);

    // ─── Regime toggle updates chart display only — no re-fetch ──────────────
    useEffect(() => {
        setRegimeType(state.regimeType);
    }, [state.regimeType]);

    // ─── Client-side search + sort (no server call on keystroke) ─────────────
    const filteredEmpList = useMemo(() => {
        let list = [...tdsEmpList];

        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter((e) =>
                (e.employeeName || "").toLowerCase().includes(q) ||
                (e.employeeId || "").toLowerCase().includes(q) ||
                (e.department || "").toLowerCase().includes(q)
            );
        }

        switch (sortBy) {
            case "department":
                list.sort((a, b) => (a.department || "").localeCompare(b.department || ""));
                break;
            case "status":
                list.sort((a, b) => (a.statusText || "").localeCompare(b.statusText || ""));
                break;
            case "tdsAmount":
                list.sort((a, b) => Number(b.totalTaxSavings || 0) - Number(a.totalTaxSavings || 0));
                break;
            default: // "name"
                list.sort((a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""));
        }

        return list;
    }, [tdsEmpList, searchText, sortBy]);

    // ─── Slab data for the currently selected regime ──────────────────────────
    const currentSlabData = useMemo(() => {
        return extractSlabData(
            { slabWiseDistribution: tdsAllRecord.slabWiseDistribution },
            regimeType
        );
    }, [tdsAllRecord.slabWiseDistribution, regimeType]);

    const navigateToTaxDeclarations = (staffId) => {
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem(
            "tdsDashboardState",
            JSON.stringify({ filters: state, searchText, sortBy, path: currentPath, timestamp: Date.now() })
        );
        navigate(`/empmanagement/user-info/${staffId}#tax-declarations`);
    };

    // ─── Export helpers ───────────────────────────────────────────────────────
    const exportToCSV = () => {
        if (!tdsEmpList.length) { toast.error("No data to export"); return; }
        setIsExporting(true);
        try {
            const csvData = tdsEmpList.map((emp) => ({
                "Employee Name": emp.employeeName || "N/A",
                "Employee ID": emp.employeeId || "N/A",
                "Department": emp.department || "N/A",
                "Status": emp.statusText || "Pending",
                "Regime Type": emp.regimeType || "N/A",
                "Total Tax Savings": emp.totalTaxSavings || "0",
                "Submitted Date": emp.submittedDate || "N/A",
                "Approved Date": emp.approvedDate || "N/A",
            }));
            const headers = Object.keys(csvData[0]).join(",");
            const rows = csvData.map((row) =>
                Object.values(row).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
            );
            const blob = new Blob([[headers, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `TDS_Report_${state.financialYear}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("CSV exported successfully!");
        } catch { toast.error("Failed to export CSV"); }
        finally { setIsExporting(false); }
    };

    const exportToPDF = () => {
        if (!tdsEmpList.length) { toast.error("No data to export"); return; }
        setIsExporting(true);
        try {
            const win = window.open("", "_blank");
            if (!win) { toast.error("Pop-up blocked. Please allow pop-ups."); setIsExporting(false); return; }
            win.document.write(`
                <html><head><title>TDS Report - ${state.financialYear}</title>
                <style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#9b51e0;color:#fff}</style>
                </head><body><h1>TDS Report - ${state.financialYear}</h1>
                <table><thead><tr><th>Name</th><th>ID</th><th>Department</th><th>Status</th><th>Regime</th><th>Tax Savings</th></tr></thead>
                <tbody>${tdsEmpList.map((e) => `<tr><td>${e.employeeName||"N/A"}</td><td>${e.employeeId||"N/A"}</td><td>${e.department||"N/A"}</td><td>${e.statusText||"Pending"}</td><td>${e.regimeType||"N/A"}</td><td>${e.totalTaxSavings||"0"}</td></tr>`).join("")}
                </tbody></table><script>window.onload=()=>window.print();<\/script></body></html>
            `);
            win.document.close();
            toast.success("PDF print dialog opened!");
        } catch { toast.error("Failed to export PDF"); }
        finally { setIsExporting(false); }
    };

    const handleExport = (format) => {
        if (isExporting || tdsEmpList.length === 0) { toast.info("Export in progress or no data"); return; }
        if (format === "PDF") exportToPDF();
        else exportToCSV(); // CSV and XLSX both use CSV for now
    };

    const downloadEmployeeReport = (emp) => {
        if (!emp) { toast.error("No employee data available"); return; }
        try {
            const row = `"${emp.employeeName||"N/A"}","${emp.employeeId||"N/A"}","${emp.department||"N/A"}","${emp.statusText||"Pending"}","${emp.regimeType||"N/A"}","${emp.totalTaxSavings||"0"}"`;
            const csv = `Employee Name,Employee ID,Department,Status,Regime,Tax Savings\n${row}`;
            const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `TDS_${emp.employeeName||emp.employeeId}_${state.financialYear}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Report downloaded for ${emp.employeeName || "employee"}`);
        } catch { toast.error("Failed to download report"); }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
    };

    const setSelectedYear = (value) => setState((prev) => ({ ...prev, financialYear: value }));

    const clearAllFilter = () => {
        setState({ financialYear: currentFynencialYear(), departmentId: "", regimeType: "old", branchId: "" });
        setRegimeType("old");
        setSearchText("");
        setSortBy("name");
        localStorage.removeItem("tdsDashboardState");
        toast.success("All filters cleared");
    };

    const applyFilters = () => {
        fetchDashboardData();
        toast.success("Filters applied!");
    };

    // ─── Column definitions — using correct API field names ───────────────────
    const columnDefs = [
        {
            headerName: "SR.NO.",
            width: 80,
            pinned: "left",
            valueGetter: "node.rowIndex + 1",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            headerClass: "ag-center-header",
        },
        {
            headerName: "Employee",
            headerClass: "ag-left-header",
            pinned: "left",
            flex: 2,
            minWidth: 260,
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start", overflow: "hidden" },
            cellRenderer: (params) => {
                const { employeeName = "N/A", employeeId = "N/A", staffId } = params.data;
                const initials = getInitials(employeeName);
                const bgColor = getInitialColor(employeeName);
                return (
                    <div
                        style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 0", cursor: "pointer", height: "100%", flex: 1, minWidth: 0 }}
                        onClick={() => navigateToTaxDeclarations(staffId)}
                        className="link-hover"
                    >
                        <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: bgColor, color: "#fff", fontWeight: "bold", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {initials}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>{employeeName}</span>
                            <span style={{ fontSize: 12, color: "#a0a0a0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>{employeeId}</span>
                        </div>
                    </div>
                );
            },
        },
        {
            headerName: "Department",
            width: 160,
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            // API returns department as ID string — show as-is or map to name if you have a lookup
            cellRenderer: (p) => <span>{p.data.department || "—"}</span>,
        },
        {
            headerName: "Status",
            width: 120,
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            // API uses statusText: "Pending" and statusColor: "warning"
            cellRenderer: (p) => {
                const { statusText = "Pending", statusColor = "warning" } = p.data;
                const colorMap = { warning: "#f59e0b", success: "#22c55e", danger: "#ef4444" };
                return (
                    <span style={{ padding: "4px 12px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${colorMap[statusColor] || "#6b7280"}22`, color: colorMap[statusColor] || "#6b7280", lineHeight: 1, display: "inline-block" }}>
                        {statusText}
                    </span>
                );
            },
        },
        {
            headerName: "Regime",
            width: 120,
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            cellRenderer: (p) => <span>{p.data.regimeType || "—"}</span>,
        },
        {
            headerName: "Tax Savings",
            width: 130,
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            // API field is totalTaxSavings (not tdsAmount)
            cellRenderer: (p) => <span>₹{Number(p.data.totalTaxSavings || 0).toLocaleString("en-IN")}</span>,
        },
        {
            headerName: "Submitted",
            width: 130,
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            cellRenderer: (p) => <span>{p.data.submittedDate || "—"}</span>,
        },
        {
            headerName: "Action",
            width: 110,
            pinned: "right",
            headerClass: "ag-center-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            cellRenderer: (p) => (
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <button className="btn btn-sm btn-outline-primary" title="View" onClick={() => navigateToTaxDeclarations(p.data.staffId)}>
                        <FaEye style={{ fontSize: 15, color: "#9b51e0" }} />
                    </button>
                    <button className="btn btn-sm btn-outline-success" title="Download" onClick={() => downloadEmployeeReport(p.data)}>
                        <FaDownload style={{ fontSize: 15, color: "#fff" }} />
                    </button>
                </div>
            ),
        },
    ];

    // ─── JSX ──────────────────────────────────────────────────────────────────
    return (
        <div className="tds-dashboard-wrap" style={{ padding: 0, boxSizing: "border-box", width: "100%", overflowX: "hidden" }}>

            {/* Filters */}
            <div className="top-header-db" style={{ padding: "8px 12px", marginBottom: 12 }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, flex: 1 }}>
                        <div className="d-flex align-items-center gap-1">
                            <span className="color-white font-weight-200" style={{ fontSize: 11, whiteSpace: "nowrap" }}>Financial Year:</span>
                            <FinancialYear selectedYear={state.financialYear} setSelectedYear={setSelectedYear} />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span className="color-white font-weight-200" style={{ fontSize: 11, whiteSpace: "nowrap" }}>Department:</span>
                            <DepartmentList name="departmentId" value={state.departmentId} onChange={handleInputChange} />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span className="color-white font-weight-200" style={{ fontSize: 11, whiteSpace: "nowrap" }}>Regime Type:</span>
                            <RegimetList name="regimeType" value={state.regimeType} onChange={handleInputChange} />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span className="color-white font-weight-200" style={{ fontSize: 11, whiteSpace: "nowrap" }}>Location:</span>
                            <BranchList name="branchId" value={state.branchId} onChange={handleInputChange} />
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <button style={{ height: 28, fontSize: 11, padding: "0 10px" }} className="border-radius-8px d-flex align-items-center gap-1 universal-btn" onClick={applyFilters}>
                            <Filter /> Apply Filters
                        </button>
                        {(state.financialYear !== currentFynencialYear() || state.departmentId || state.regimeType !== "old" || state.branchId) && (
                            <button style={{ height: 28, fontSize: 11, padding: "0 10px" }} className="border-radius-8px d-flex align-items-center gap-1 universal-sec-btn" onClick={clearAllFilter}>
                                <Cross /> Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ padding: "0 12px 16px 12px", overflowX: "hidden", width: "100%", boxSizing: "border-box" }}>

                {/* Stat Cards */}
                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-purple" /></div>
                ) : (
                    <div className="stats-wrapper mb-3">
                        <div className="stats-grid">
                            <div className="stat-box box-1">
                                <FaFileAlt className="stat-icon text-purple" />
                                <h2 className="font-weight-200 text-purple">{tdsAllRecord.totalTDSSubmitted}</h2>
                                <p className="stat-title">Total 12BB Submitted</p>
                                <p className="stat-sub green-text font-weight-200">↑ {tdsAllRecord.totalTDSTrend}</p>
                            </div>
                            <div className="stat-box box-2">
                                <FaClock className="stat-icon yellow-text" />
                                <h2 className="font-weight-200 yellow-text">{tdsAllRecord.pendingVerifications}</h2>
                                <p className="stat-title">Pending Verifications</p>
                                <p className="stat-sub text-red">▲ {tdsAllRecord.totalEmployees} total employees</p>
                            </div>
                            <div className="stat-box box-3">
                                <FaCheckCircle className="stat-icon green-text" />
                                <h2 className="font-weight-200 green-text">{tdsAllRecord.approved}</h2>
                                <p className="stat-title">Approved</p>
                                <p className="stat-sub green-text">↑ {tdsAllRecord.approvedRate}</p>
                            </div>
                            <div className="stat-box box-4">
                                <FaTimesCircle className="stat-icon text-red" />
                                <h2 className="font-weight-200 text-red">{tdsAllRecord.rejected}</h2>
                                <p className="stat-title">Rejected</p>
                                <p className="stat-sub text-gray">{tdsAllRecord.rejectedRate}</p>
                            </div>
                            <div className="stat-box box-5">
                                <FaStopwatch className="stat-icon text-info" />
                                <h2 className="font-weight-200 text-info">{tdsAllRecord.avgProcessingTime}</h2>
                                <p className="stat-title">Avg Processing Time</p>
                                <p className="stat-sub blue-text">Days per submission</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Slab-wise + Deduction Breakdown */}
                <Row className="mt-3 g-3">
                    <Col xs="12" md="8">
                        <div className="setting-border height-auto p-3">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="text-purple font-weight-200 mb-0" style={{ fontSize: 13 }}>Slab-wise Tax Distribution</h5>
                                <div className="d-flex gap-1">
                                    <button className={`border-radius-8px px-3 py-1 ${regimeType === "old" ? "universal-btn" : "universal-sec-btn"}`} style={{ fontSize: 11 }} onClick={() => setState((p) => ({ ...p, regimeType: "old" }))}>Old Regime</button>
                                    <button className={`border-radius-8px px-3 py-1 ${regimeType === "new" ? "universal-btn" : "universal-sec-btn"}`} style={{ fontSize: 11 }} onClick={() => setState((p) => ({ ...p, regimeType: "new" }))}>New Regime</button>
                                </div>
                            </div>
                            {currentSlabData.length > 0 ? (
                                <BarChart regimeType={regimeType} data={currentSlabData} />
                            ) : (
                                <div className="text-center py-3"><p className="color-gray" style={{ fontSize: 12 }}>No slab-wise data available</p></div>
                            )}
                        </div>
                    </Col>
                    <Col xs="12" md="4">
                        <div className="setting-border height-auto p-3">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="text-purple font-weight-200 mb-0" style={{ fontSize: 13 }}>Deduction Breakdown</h5>
                                <CircleInfo class_name="color-gray" />
                            </div>
                            {tdsAllRecord.deductionBreakdown?.length > 0 ? (
                                <DeductionBreakDown data={tdsAllRecord.deductionBreakdown} />
                            ) : (
                                <div className="text-center py-3"><p className="color-gray" style={{ fontSize: 12 }}>No deduction data available</p></div>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* PAN Compliance + Processing Trends */}
                <Row className="mt-3 g-3">
                    <Col xs="12" md="6">
                        <div className="setting-border height-auto p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <h5 className="text-purple font-weight-200 mb-0" style={{ fontSize: 13 }}>Rent ₹1L Compliance</h5>
                                <span className="color-gray font-weight-200" style={{ fontSize: 11 }}>PAN Status</span>
                            </div>
                            <Row className="g-2">
                                <Col xs="6">
                                    <div className="p-2 rounded text-center" style={{ background: "rgba(43,3,60,0.1)" }}>
                                        <h4 className="text-green font-weight-200 mb-1" style={{ fontSize: 22 }}>{tdsAllRecord.panPresentPercentage}%</h4>
                                        <p className="color-gray mb-0" style={{ fontSize: 11 }}>PAN Present</p>
                                        <p className="text-green mb-0" style={{ fontSize: 12, fontWeight: 500 }}>{tdsAllRecord.panPresentCount} Employees</p>
                                    </div>
                                </Col>
                                <Col xs="6">
                                    <div className="p-2 rounded text-center" style={{ background: "rgba(49,6,90,0.1)" }}>
                                        <h4 className="text-red font-weight-200 mb-1" style={{ fontSize: 22 }}>{tdsAllRecord.panMissingPercentage}%</h4>
                                        <p className="color-gray mb-0" style={{ fontSize: 11 }}>PAN Missing</p>
                                        <p className="text-red mb-0" style={{ fontSize: 12, fontWeight: 600 }}>{tdsAllRecord.panMissingCount} Employees</p>
                                    </div>
                                </Col>
                            </Row>
                            <div className="mt-2">
                                <ProgressBar now={tdsAllRecord.panPresentPercentage} style={{ height: 7 }} className="custom-progress" />
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="color-gray" style={{ fontSize: 10 }}>Compliant</span>
                                    <span className="color-gray" style={{ fontSize: 10 }}>Non-Compliant</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6">
                        <div className="setting-border height-auto p-3" style={{ overflow: "hidden" }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <h5 className="text-purple font-weight-200 mb-0" style={{ fontSize: 13 }}>Processing Time Trends</h5>
                                <span className="color-gray font-weight-200" style={{ fontSize: 11 }}>Last 6 months</span>
                            </div>
                            <div style={{ height: 200, background: "rgba(30,30,50,0.3)", borderRadius: 8, padding: "6px 0", overflow: "hidden" }}>
                                {tdsAllRecord.processingTrends.length > 0 ? (
                                    <BarChart data={tdsAllRecord.processingTrends} />
                                ) : (
                                    <div className="h-100 d-flex align-items-center justify-content-center">
                                        <p className="color-gray" style={{ fontSize: 12 }}>No trend data available yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Export Section */}
                <div className="setting-border height-auto mt-3 mb-3 p-3">
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <div>
                            <h5 className="text-purple font-weight-200 mb-0" style={{ fontSize: 13 }}>Export Reports</h5>
                            <p className="color-gray font-weight-200 mb-0" style={{ fontSize: 11 }}>Download in your preferred format</p>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            <button style={{ fontSize: 11, padding: "4px 10px" }} className="d-flex align-items-center gap-1 universal-btn-red border-radius-8px" onClick={() => handleExport("PDF")} disabled={isExporting || !tdsEmpList.length}><FilePdf /> PDF</button>
                            <button style={{ fontSize: 11, padding: "4px 10px" }} className="d-flex align-items-center gap-1 universal-btn-green border-radius-8px" onClick={() => handleExport("XLSX")} disabled={isExporting || !tdsEmpList.length}><FileExcel /> XLSX</button>
                            <button style={{ fontSize: 11, padding: "4px 10px" }} className="d-flex align-items-center gap-1 universal-btn-info border-radius-8px" onClick={() => handleExport("CSV")} disabled={isExporting || !tdsEmpList.length}><FileCsv /> CSV</button>
                            <button style={{ fontSize: 11, padding: "4px 10px" }} className="d-flex align-items-center gap-1 universal-btn border-radius-8px" onClick={() => handleExport("CSV")} disabled={isExporting || !tdsEmpList.length}><Download /> Bulk Export</button>
                        </div>
                    </div>
                </div>

                {/* Employee Table */}
                <Widgets
                    icon={<span className="color-white font-weight-200" style={{ fontSize: 12 }}>Employee Details ({filteredEmpList.length})</span>}
                    button={
                        <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                            <div style={{ position: "relative" }}>
                                <Search class_name="search-absolute" />
                                <input
                                    placeholder="Search Employees..."
                                    className="universal-input padding-left-35-px on-focus-outline-none"
                                    style={{ width: 180, fontSize: 11, height: 28 }}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <Form.Select
                                className="universal-input on-focus-outline-none"
                                style={{ width: 160, fontSize: 11, height: 28, padding: "0 8px" }}
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="name">Sort by name</option>
                                <option value="department">Sort by department</option>
                                <option value="status">Sort by status</option>
                                <option value="tdsAmount">Sort by tax savings</option>
                            </Form.Select>
                        </div>
                    }
                    body={
                        <>
                            <div className="ag-theme-quartz custom-grid mt-2" style={{ height: "calc(100vh - 380px)", width: "100%" }}>
                                {loading ? (
                                    <TableSkeleton />
                                ) : filteredEmpList.length > 0 ? (
                                    <AgGridReact
                                        rowData={filteredEmpList}
                                        columnDefs={columnDefs}
                                        pagination={true}
                                        paginationPageSize={paginationPageSize}
                                        paginationPageSizeSelector={paginationPageSizeSelector}
                                        onGridReady={onGridReady}
                                        domLayout="normal"
                                        defaultColDef={{
                                            resizable: true,
                                            sortable: true,
                                            filter: true,
                                            cellStyle: { display: "flex", alignItems: "center", padding: "0 8px", fontSize: 12 },
                                        }}
                                        rowHeight={52}
                                        headerHeight={36}
                                        animateRows={true}
                                        onFirstDataRendered={(p) => setTimeout(() => p.api.sizeColumnsToFit(), 100)}
                                        onGridSizeChanged={(p) => p.api.sizeColumnsToFit()}
                                    />
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="color-gray" style={{ fontSize: 12 }}>
                                            {searchText ? `No results for "${searchText}"` : "No employee records found"}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {filteredEmpList.length > 0 && gridApi && (
                                <div className="d-flex align-items-center justify-content-between custom-pagination">
                                    <CustomPagination gridApi={gridApi} />
                                </div>
                            )}
                        </>
                    }
                />
            </div>
        </div>
    );
};

export default Dashboard;